import React, { useContext, useEffect, useState } from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import "./SentimentDivergingBars.css";
import { ConceptsContext } from "../../../contexts";
import { useGetSentimentsMutation } from "../../../store/api/lumi/sentiment";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";

export function SentimentShareBarChart(props) {
    const [divergingbarsData, setDivergingbarsData] = useState([]);
    const chartMargin = {
        top: 15,
        right: 20,
        left: 0,
        bottom: 60,
    };
    const chartWidth = 700;
    const chartHeight = 450;
    const barWidth = 40;
    const neutralColor = "#d8d8d8";
    const [isLoading, seIstLoading] = useState(false);
    const [getSentiments] = useGetSentimentsMutation();

    async function getSentimentsData() {
        seIstLoading(true);
        const conceptFilter = getConceptFilter(props);

        const { data: dataConcepts } = await getSentiments({
            id: props.id,
            concept_selector: conceptFilter,
            filter: props.filters,
        });
        props.setFilterDocCount(dataConcepts.filter_count);
        let results = dataConcepts.match_counts;
        if (props && props.conceptList) {
            props.setLocalConceptOptions(results);
        }
        setDivergingbarsData(results);
        seIstLoading(false);
    }

    useEffect(() => {
        seIstLoading(true);
        const debouncedGetVolume = debounce(getSentimentsData, 1000);

        debouncedGetVolume();

        return () => {
            debouncedGetVolume.cancel();
        };
    }, [
        props.conceptList,
        props.seletctedType,
        props.selectedPageSize,
        props.filters,
        props.conceptTypeDay,
        props.selectedLocalConcepts.length,
    ]);

    // Custom tooltip formatter function
    const renderTooltipContent = (tooltipData) => {
        const { payload } = tooltipData;
        if (payload && payload.length) {
            const { name, sentiment_share } = payload[0].payload;
            return (
                <div className="custom-tooltip">
                    <div className="tooltip-title">{name}</div>
                    <div>
                        <span className="tooltip-label">Positive:</span>{" "}
                        <span className="tooltip-value positive">
                            {sentiment_share.positive.toFixed(2)}
                        </span>
                    </div>
                    <div>
                        <span className="tooltip-label">Neutral:</span>{" "}
                        <span className="tooltip-value neutral">
                            {sentiment_share.neutral.toFixed(2)}
                        </span>
                    </div>
                    <div>
                        <span className="tooltip-label">Negative:</span>{" "}
                        <span className="tooltip-value negative">
                            {sentiment_share.negative.toFixed(2)}
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };

    const renderCustomAxisTick = (tickProps) => {
        const { x, y, payload } = tickProps;
        const maxLabelLength = 15;
        let label = payload.value;

        if (label.length > maxLabelLength) {
            label = `${label.substring(0, maxLabelLength)}...`;
        }

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={0}
                    y={0}
                    dy={16}
                    textAnchor="end"
                    fontSize={16}
                    fontWeight={600}
                    transform="rotate(-45)"
                >
                    {label}
                </text>
            </g>
        );
    };

    const { activeConcepts, setActiveConcepts } = useContext(ConceptsContext);
    const focusOnConcept = ({ active, payload }) => {
        //check if concept is already active first
        if (activeConcepts.includes(payload.concept)) {
            setActiveConcepts(
                activeConcepts.filter((concept) => concept !== payload.concept)
            );
            return;
        }
        setActiveConcepts([...activeConcepts, payload.concept]);
    };
    if (isLoading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    return (
        <div className="diverging-container"  style={{height: props.widget.layout.h * 39 - 150}}>
            <ResponsiveContainer width="100%" >
                <BarChart
                    width={chartWidth}
                    height={chartHeight}
                    data={divergingbarsData}
                    margin={chartMargin}
                    stackOffset="sign"
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <defs>
                        <linearGradient
                            id="positiveGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="5%"
                                stopColor="#0074f3"
                                stopOpacity={0.9}
                            />
                            <stop
                                offset="95%"
                                stopColor="#0074f3"
                                stopOpacity={0.5}
                            />
                        </linearGradient>
                        <linearGradient
                            id="negativeGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="5%"
                                stopColor="#FF5733"
                                stopOpacity={0.8}
                            />
                            <stop
                                offset="95%"
                                stopColor="#c70039"
                                stopOpacity={0.8}
                            />
                        </linearGradient>
                        {/* Define a gradient for neutral bars if needed */}
                        <linearGradient
                            id="neutralGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="5%"
                                stopColor="#d8d8d8"
                                stopOpacity={1}
                            />
                            <stop
                                offset="95%"
                                stopColor="#a8a8a8"
                                stopOpacity={1}
                            />
                        </linearGradient>
                    </defs>
                    <XAxis
                        dataKey="name"
                        interval={0}
                        tick={renderCustomAxisTick}
                    />
                    <YAxis   tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }} />
                    <Tooltip content={renderTooltipContent} />
                    <Bar
                        dataKey={(divergingbarsData) =>
                            -divergingbarsData.sentiment_share.neutral / 2
                        }
                        fill={neutralColor}
                        barSize={barWidth}
                        stackId="stack"
                        onClick={focusOnConcept}
                        cursor={"pointer"}
                    />
                    <Bar
                        dataKey={(divergingbarsData) =>
                            -divergingbarsData.sentiment_share.negative
                        }
                        fill="url(#negativeGradient)"
                        barSize={barWidth}
                        stackId="stack"
                        onClick={focusOnConcept}
                        cursor={"pointer"}
                    />
                    <Bar
                        dataKey={(divergingbarsData) =>
                            divergingbarsData.sentiment_share.neutral / 2
                        }
                        fill={neutralColor}
                        barSize={barWidth}
                        stackId="stack"
                        onClick={focusOnConcept}
                        cursor={"pointer"}
                    />
                    <Bar
                        dataKey={(divergingbarsData) =>
                            divergingbarsData.sentiment_share.positive
                        }
                        fill="url(#positiveGradient)"
                        barSize={barWidth}
                        stackId="stack"
                        onClick={focusOnConcept}
                        cursor={"pointer"}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
